import { Component, Inject, OnInit, } from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef, } from '@angular/material/bottom-sheet';
import { NgClass } from '@angular/common';
import { ConnectionService } from '@services/connection-service';
import { EventLoggerService } from '@services/event-logger-service';
import { AppConfig } from '../../../../app.config';
import { Router } from '@angular/router';

export type TimeSlot = 'working' | 'evening' | 'early';

export interface RequestCallBottomsheetData {
  regimenId: string;
  regimenClass: string;
  noOfHours: number;
  slot: TimeSlot;
  rehaulView?: boolean,
}

@Component({
  selector: 'app-pending-call-bottomsheet',
  standalone: true,
  imports: [NgClass],
  templateUrl: './request-call-bottomsheet.component.html',
})
export class RequestCallBottomsheetComponent implements OnInit {
  public userNumber!: number;
  public loading = false;
  rehaulView: boolean = false;
  regimenClass: string;
  internalUser: boolean = false;
  protected deviceHeight: number;
  protected readonly minDeviceHeight: number = 670;
  private userLanguage: string = 'en';

  /** Hour label translations mapping */
  private readonly hourLabelMap: Record<string, string> = {
    'en': 'hours',
    'hi': 'घंटे में',
    'mr': 'तासात',
    'te': 'గంటలలో',
    'kn': 'ಗಂಟೆಗಳಲ್ಲಿ',
    'ta': 'மணி நேரத்தில்',
    'ml': 'മണിക്കൂറുകൾക്കുള്ളിൽ',
    'bn': 'ঘন্টায়',
  };

  constructor(
    @Inject(MAT_BOTTOM_SHEET_DATA)
    private readonly data: RequestCallBottomsheetData,
    public readonly appConfig: AppConfig,
    private readonly bottomSheetRef: MatBottomSheetRef<RequestCallBottomsheetComponent>,
    public readonly conn: ConnectionService,
    private readonly eventLogger: EventLoggerService,
    private readonly router: Router,
  ) {
    this.deviceHeight = window.innerHeight;
  }

  ngOnInit(): void {
    const user = this.conn.getActingUser();
    this.userNumber = user.get('MobileNumber');
    this.internalUser = this.conn.isInternalUser();
    this.regimenClass = this.data.regimenClass;
    this.userLanguage = user.get('languagePreference') || this.appConfig.Shared.Languages.EN;
    if (this.data.rehaulView ) {
      this.rehaulView = true;
    }
  }

  /** Template flags */
  get showWorkingHours(): boolean {
    return this.data.slot === 'working';
  }
  get showTomorrowMorning(): boolean {
    return this.data.slot === 'evening';
  }
  get showAfterNineAm(): boolean {
    return this.data.slot === 'early';
  }

  /** get hours */
  get hours(): number {
    return this.data.noOfHours;
  }

  /** Dynamic hour label based on user language */
  get hourLabel(): string {
    return this.hourLabelMap[this.userLanguage] || this.hourLabelMap['en'];
  }

  /** Close without outcome */
  closePopup(): void {
    this.bottomSheetRef.dismiss();
  }

  closeAndNavigateToHome(): void {
    this.closePopup()
    if (this.rehaulView) {
      this.router.navigate(['/user'],
        {queryParams: { tab: 'home' }},
      );
    }
  }

  /** Fire click, call API, and dismiss with result */
  async requestCall(): Promise<void> {
    this.logRequestClick();
    this.loading = true;

    try {
      const success = await this.conn.createUnpaidUserDoctorCall(this.data.regimenId);

      if (success) {
        this.bottomSheetRef.dismiss({ pendingCallCreated: true });
      } else {
        this.bottomSheetRef.dismiss();
      }
    } finally {
      this.loading = false;
    }
  }

  /** Encapsulated event‐logger call */
  private logRequestClick(): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'regimen_unpaid_user_call_request_popup' }));
  }
}
